import { IonGrid, IonRow } from '@ionic/react'
import React, { FC, useEffect, useRef, useState } from 'react'

import { ScreenSection } from 'mytours-read-common'
import { useOnline } from 'mobile_web/modules/offline/useOnline'
import { getDirMode } from 'mobile_web/modules/platform'
import strings from 'mobile_web/strings'
import SectionHeader from './SectionHeader'

import '../styles/sectionForm.less'

interface Props {
	section: ScreenSection
}

// A frame that starts at zero never gets a ResizeObserver callback in some browsers.
const INITIAL_HEIGHT = 420

// A sanity bound, not a usability one: some 25 viewport heights, so it truncates no real form and
// only catches a runaway number that would leave the page unscrollable.
const MAX_HEIGHT = 20000

// `allow-top-navigation` is withheld, so script inside the frame cannot navigate the visitor away.
const FRAME_SANDBOX =
	'allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads'

// A cached embed_url may not parse, and throwing here would blank every section on the screen.
const originOf = (url: string | undefined): string | undefined => {
	if (!url) return undefined

	try {
		return new URL(url).origin
	} catch {
		return undefined
	}
}

// collapsable and exposable_content are ignored, as in 11 of the 14 section types the builder
// offers them on: clamping a frame that reports its height late would cut off the submit button.
const SectionForm: FC<Props> = ({ section }) => {
	const online = useOnline()
	const frameRef = useRef<HTMLIFrameElement>(null)
	const [height, setHeight] = useState(INITIAL_HEIGHT)

	const { embed_url: embedUrl, form_title: formTitle, title } = section
	const origin = originOf(embedUrl)

	// A new embed_url would otherwise sit at the last form's height until its first message. Keyed on
	// the url, not the origin, since two forms normally share a host.
	useEffect(() => {
		setHeight(INITIAL_HEIGHT)
	}, [embedUrl])

	useEffect(() => {
		if (!origin || !online) return

		// The form posts to '*', so any framed page could drive this height without both checks.
		const onMessage = (event: MessageEvent) => {
			if (event.origin !== origin) return
			if (event.source !== frameRef.current?.contentWindow) return
			if (event.data?.type !== 'form:height') return

			const reported = Number(event.data.height)
			if (Number.isFinite(reported) && reported > 0) setHeight(Math.min(reported, MAX_HEIGHT))
		}

		window.addEventListener('message', onMessage)
		return () => window.removeEventListener('message', onMessage)
	}, [origin, online])

	if (!embedUrl || !origin) return null

	return (
		<IonGrid className="section-form" dir={getDirMode()}>
			{!!title && <SectionHeader title={title} />}
			<IonRow className="block-content" data-cy="story-screen-section-content">
				{online ? (
					<iframe
						ref={frameRef}
						className="form-frame"
						src={embedUrl}
						sandbox={FRAME_SANDBOX}
						title={formTitle || title}
						style={{ height: `${height}px` }}
					/>
				) : (
					<div className="form-needs-connection" role="note">
						<i className="fal fa-wifi-slash" aria-hidden="true" />
						<span>{strings.needs_connection}</span>
					</div>
				)}
			</IonRow>
		</IonGrid>
	)
}

export default SectionForm
